import {
  applyAxisConversion,
  AR_MEDIUM,
  getProfileById,
  optimize,
  probe,
  recommendDetail,
  summarizeModel,
} from 'trace-viewer/model-utils';
import type {
  AxisConversion,
  MaterialOverride,
  ModelProbeReport,
  ModelSummary,
  OptimizationChangeLog,
  OptimizationProfile,
  OptimizationProfileId,
} from 'trace-viewer/model-utils';
import { downloadBlob, resetDisclosures, wireDisclosures, wireFileInput } from '../shared/ui';
import { renderApp } from './render';
import { formatErrorLabel } from './render-adjust';
import { isStale, settingsKey } from './result-state';
import { previewViewer } from './preview';
import { sliderToDetail, sliderToError } from './geometry-slider';

export interface UiState {
  file: File | null;
  report: ModelProbeReport | null;
  selectedProfile: OptimizationProfile;
  /** Convert a Z-up source model to glTF's Y-up convention on optimize. */
  axisConversion: AxisConversion;
  /** Uniform scale baked into the geometry on optimize (1 = no rescale). */
  scaleFactor: number;
  /**
   * Which rescale control is active. 'custom' keeps the Custom radio selected
   * even when its factor is 1 (which would otherwise look like the None preset).
   */
  scaleMode: 'preset' | 'custom';
  /** Per-material edits, keyed by material name. Seeded from recommendations. */
  materialOverrides: Record<string, MaterialOverride>;
  phase: 'idle' | 'probing' | 'done' | 'error';
  error: string | null;
  optimization: {
    running: boolean;
    stage: string;
    progress: number;
    output: Blob | null;
    outputName: string | null;
    bytesSaved: number;
    percentReduced: number;
    /** Stats of the optimized output — drives the post-optimize verdict. */
    report: ModelProbeReport | null;
    /** Detailed log of actions taken, shown in the result breakdown. */
    changes: OptimizationChangeLog | null;
    /** `settingsKey()` of the run that produced `output` — detects stale results. */
    settingsKey: string | null;
    error: string | null;
  };
}

const idleOptimization: UiState['optimization'] = {
  running: false,
  stage: '',
  progress: 0,
  output: null,
  outputName: null,
  bytesSaved: 0,
  percentReduced: 0,
  report: null,
  changes: null,
  settingsKey: null,
  error: null,
};

const initialState: UiState = {
  file: null,
  report: null,
  selectedProfile: AR_MEDIUM,
  axisConversion: 'none',
  scaleFactor: 1,
  scaleMode: 'preset',
  materialOverrides: {},
  phase: 'idle',
  error: null,
  optimization: idleOptimization,
};

let state: UiState = { ...initialState };

const root = document.getElementById('app')!;

function setState(patch: Partial<UiState>): void {
  state = { ...state, ...patch };
  render();
}

function setOptimization(patch: Partial<UiState['optimization']>): void {
  state = { ...state, optimization: { ...state.optimization, ...patch } };
  render();
}

function currentSummary(): ModelSummary | null {
  // Once optimized, judge the OPTIMIZED output — otherwise the summary keeps
  // saying "needs optimization" for a model we just optimized.
  const report = state.optimization.report ?? state.report;
  if (!report) return null;
  // One status from both halves — budget AND intrinsic issues. Judging on the
  // budget alone once showed "Ready for AR" for a file that wouldn't even open.
  return summarizeModel(report, state.selectedProfile);
}

function render(): void {
  renderApp(root, {
    state,
    summary: currentSummary(),
    dirty: isStale(state.optimization.settingsKey, state.optimization.output != null, state),
  });
}

async function handleFile(file: File): Promise<void> {
  resetDisclosures();
  setState({
    file,
    report: null,
    phase: 'probing',
    error: null,
    // A new model starts with no orientation/scale edits carried over.
    axisConversion: 'none',
    scaleFactor: 1,
    scaleMode: 'preset',
    materialOverrides: {},
    optimization: idleOptimization,
  });
  // Drop the previous model from both preview panes BEFORE probing. Clearing the
  // optimization state alone isn't enough: the panes hold live WebGL content, so
  // a file that fails to probe (unsupported extension, corrupt GLB) would leave
  // the last file's optimized result on screen next to the new file's name.
  await previewViewer.clear();
  try {
    const report = await probe(file);
    // Seed the recommended material edits and geometry detail. The error ceiling
    // stays on "Auto" (errorCeiling undefined) — optimize picks it per mesh
    // (tight for precision, loose for dense); the user can override in Adjust.
    const { detail } = recommendDetail(report);
    setState({
      report,
      phase: 'done',
      materialOverrides: seedOverridesFromRecommendations(report),
      selectedProfile: { ...state.selectedProfile, geometryDetail: detail, errorCeiling: undefined },
    });
    // Reveal the inline preview slot and load the original (raw) into the before
    // pane once. handleOptimize re-orients it only if axis conversion is on.
    showPreview();
    await loadOriginalPreview();
    // Auto-run one optimization pass so the Optimized preview + download are
    // ready immediately, reflecting the recommended settings. Skipped when the
    // probe bailed: our glTF reader already failed to open this file, so
    // optimize() would only fail the same way and bury the real diagnosis
    // (e.g. a required Draco extension) under a raw parser error.
    if (!report.bailout) void handleOptimize();
  } catch (err) {
    // Nothing loaded — hide the (now empty) preview rather than showing two
    // blank viewports under the failed file.
    previewViewer.hide();
    setState({ phase: 'error', error: (err as Error).message ?? 'Could not read this model' });
  }
}

/**
 * Pre-select each material's recommended edits so the editor opens with the
 * suggested fixes applied (the original values are still shown alongside).
 * Only materials with a recommendation get an entry; the rest stay unedited.
 */
function seedOverridesFromRecommendations(report: ModelProbeReport): Record<string, MaterialOverride> {
  const overrides: Record<string, MaterialOverride> = {};
  for (const mat of report.materials.materials ?? []) {
    const rec = mat.recommended;
    if (!rec || (rec.alphaMode === undefined && rec.doubleSided === undefined && rec.baseColorFormat === undefined)) {
      continue;
    }
    overrides[mat.name] = {
      ...(rec.alphaMode !== undefined ? { alphaMode: rec.alphaMode } : {}),
      ...(rec.doubleSided !== undefined ? { doubleSided: rec.doubleSided } : {}),
      ...(rec.baseColorFormat !== undefined ? { baseColorFormat: rec.baseColorFormat } : {}),
    };
  }
  return overrides;
}

/**
 * Apply a material-control edit into state.materialOverrides. A control value
 * equal to the material's authored value clears that override field (so the
 * model only carries genuine deltas). Re-renders so the "changed" tags and
 * dependent controls update.
 */
function handleMaterialEdit(el: HTMLSelectElement | HTMLInputElement): void {
  const name = el.dataset.material;
  const field = el.dataset.field;
  if (!name || !field || !state.report) return;
  const mat = state.report.materials.materials.find((m) => m.name === name);
  if (!mat) return;

  const overrides = { ...state.materialOverrides };
  const current: MaterialOverride = { ...(overrides[name] ?? {}) };

  if (field === 'alphaMode') {
    const value = (el as HTMLSelectElement).value as MaterialOverride['alphaMode'];
    if (value === mat.alphaMode) delete current.alphaMode;
    else current.alphaMode = value;
  } else if (field === 'doubleSided') {
    const value = (el as HTMLInputElement).checked;
    if (value === mat.doubleSided) delete current.doubleSided;
    else current.doubleSided = value;
  } else if (field === 'baseColorFormat') {
    const value = (el as HTMLSelectElement).value as MaterialOverride['baseColorFormat'];
    if (value === mat.baseColorFormat || value === 'keep') delete current.baseColorFormat;
    else current.baseColorFormat = value;
  }

  if (Object.keys(current).length === 0) delete overrides[name];
  else overrides[name] = current;

  setState({ materialOverrides: overrides });
}

/** Reveal the inline preview slot (rendered inside #app by renderApp). */
function showPreview(): void {
  const slot = document.getElementById('preview-slot');
  if (slot) previewViewer.show(slot);
}

/** Load the original source (raw) into the "before" pane — call once per file. */
async function loadOriginalPreview(): Promise<void> {
  if (!state.file) return;
  await previewViewer.loadBefore(state.file);
}

async function handleOptimize(): Promise<void> {
  if (!state.file || !state.report || state.optimization.running) return;
  // A re-run already has a model in the "after" pane — keep the current camera
  // angle so the user can see how the new settings affect the same view.
  const isRerun = previewViewer.hasContent;
  const key = settingsKey(state);
  // Keep the previous output visible (and downloadable) until the new one lands.
  setOptimization({ running: true, stage: 'Starting', progress: 0, error: null });
  try {
    const result = await optimize(state.file, {
      profile: state.selectedProfile,
      axisConversion: state.axisConversion,
      scaleFactor: state.scaleFactor,
      materialOverrides: state.materialOverrides,
      onProgress: (stage, ratio) => {
        setOptimization({ stage, progress: ratio });
      },
    });
    setOptimization({
      running: false,
      progress: 1,
      stage: 'done',
      output: result.blob,
      outputName: result.filename,
      bytesSaved: result.bytesSaved,
      percentReduced: result.percentReduced,
      report: result.report,
      changes: result.changes,
      settingsKey: key,
    });
    // Re-orient the "before" pane only if the axis conversion changed (no-op
    // otherwise — the input preview isn't reloaded on every optimize), then drop
    // the optimized GLB into the "after" pane. Sequential, so the two loads don't
    // race on the preview's shared (serial) viewer init.
    void (async () => {
      await previewViewer.setBeforeConversion(state.axisConversion, applyAxisConversion, state.scaleFactor);
      await previewViewer.loadAfter(result.blob, isRerun);
    })();
  } catch (err) {
    setOptimization({ running: false, error: (err as Error).message ?? 'Optimizing failed' });
  }
}

root.addEventListener('click', (e) => {
  const target = e.target as HTMLElement;

  // Clicking a texture thumbnail opens the full-size popover.
  const thumbBtn = target.closest<HTMLElement>('[data-texture-preview]');
  if (thumbBtn) {
    openTextureModal(thumbBtn.dataset.texturePreview ?? '', thumbBtn.dataset.textureCaption ?? '');
    return;
  }

  // Clicking anywhere on the open modal closes it.
  if (target.closest('#texture-modal')) {
    closeTextureModal();
    return;
  }

  const actionEl = target.closest<HTMLElement>('[data-action]');
  const action = actionEl?.dataset.action;

  if (action === 'optimize') {
    void handleOptimize();
  } else if (action === 'use-recommended-detail') {
    if (state.report) {
      const { detail } = recommendDetail(state.report);
      setState({ selectedProfile: { ...state.selectedProfile, geometryDetail: detail } });
    }
  } else if (action === 'reset-error-ceiling') {
    // Clear the explicit override → optimize auto-picks per mesh again.
    setState({ selectedProfile: { ...state.selectedProfile, errorCeiling: undefined } });
  } else if (action === 'download-output' && state.optimization.output && state.optimization.outputName) {
    downloadBlob(state.optimization.output, state.optimization.outputName);
  }
});

/**
 * Apply a slider's value into state.selectedProfile WITHOUT re-rendering.
 * Re-rendering replaces the DOM via innerHTML, which destroys the <input>
 * element mid-drag and drops the pointer capture — so the slider could only
 * ever move one step per click. We mutate state quietly here and patch the
 * visible value in place; the full render() happens on `change` (drag end /
 * click), once the element no longer needs to survive.
 */
function applySettingToState(input: HTMLInputElement): void {
  const setting = input.dataset.setting;
  const value = parseFloat(input.value);
  const custom = { ...state.selectedProfile };

  if (setting === 'geometryDetail') {
    // Slider value IS the detail %; sliderToDetail clamps to a retention ratio.
    custom.geometryDetail = sliderToDetail(value);
  } else if (setting === 'jpgQuality') {
    custom.jpgQuality = Math.round(value);
  } else if (setting === 'errorCeiling') {
    // Slider position (0–100, hundredths of a %) → error ceiling fraction.
    custom.errorCeiling = sliderToError(value);
  }

  // Mutate state silently — no render() so the slider element survives the drag.
  state = { ...state, selectedProfile: custom };
}

/** Update the live value shown beside a slider without touching the slider itself. */
function updateSliderValue(input: HTMLInputElement): void {
  const setting = input.dataset.setting;
  const valueEl = root.querySelector(`[data-value-for="${setting}"]`);
  if (!valueEl) return;
  const v = parseFloat(input.value);
  if (setting === 'geometryDetail') {
    const detail = Math.round(v);
    valueEl.textContent = detail >= 100 ? '100% (unchanged)' : `${detail}%`;
  } else if (setting === 'jpgQuality') {
    valueEl.textContent = `${Math.round(v)}%`;
  } else if (setting === 'errorCeiling') {
    valueEl.textContent = formatErrorLabel(sliderToError(v));
  }
}

root.addEventListener('change', (e) => {
  const target = e.target as HTMLElement;
  if (target.matches('input[name="quality"]')) {
    const id = (target as HTMLInputElement).value as OptimizationProfileId;
    setState({ selectedProfile: getProfileById(id) ?? AR_MEDIUM });
    // Quality is the headline choice — show its result straight away rather
    // than asking for a second click. Finer edits wait for "Apply changes".
    if (state.report && !state.report.bailout) void handleOptimize();
    return;
  }
  if (target.matches('input[name="texture-res"]')) {
    const px = parseInt((target as HTMLInputElement).value, 10);
    if (px > 0) setState({ selectedProfile: { ...state.selectedProfile, maxTextureSideLength: px } });
    return;
  }
  if (target.matches('input[data-setting="axisConversion"]')) {
    const checked = (target as HTMLInputElement).checked;
    // Deferred: the conversion is applied on the next optimize, not
    // immediately, so the before/after preview stays consistent.
    setState({ axisConversion: checked ? 'zUpToYUp' : 'none' });
    return;
  }
  if (target.matches('input[data-action="set-scale"]')) {
    // A preset rescale radio (None / cm→m / mm→m). Deferred to next optimize.
    const factor = parseFloat((target as HTMLInputElement).dataset.factor ?? '1');
    setState({ scaleMode: 'preset', scaleFactor: Number.isFinite(factor) && factor > 0 ? factor : 1 });
    return;
  }
  if (target.matches('input[data-action="set-scale-custom"]')) {
    // Selecting "Custom" stays selected (scaleMode='custom') and uses the
    // field's value, defaulting to a no-op 1.0 when it's empty.
    const field = target
      .closest('label')
      ?.querySelector<HTMLInputElement>('input[data-setting="scaleCustom"]');
    const factor = field ? parseFloat(field.value) : NaN;
    setState({ scaleMode: 'custom', scaleFactor: Number.isFinite(factor) && factor > 0 ? factor : 1 });
    return;
  }
  if (target.matches('input[data-setting="scaleCustom"]')) {
    const factor = parseFloat((target as HTMLInputElement).value);
    setState({ scaleMode: 'custom', scaleFactor: Number.isFinite(factor) && factor > 0 ? factor : 1 });
    return;
  }
  if (target.matches('[data-material][data-field]')) {
    handleMaterialEdit(target as HTMLSelectElement | HTMLInputElement);
    return;
  }
  if (target.matches('input[type="range"][data-setting]')) {
    // Drag finished (or click) — commit and do the single full re-render here.
    applySettingToState(target as HTMLInputElement);
    render();
  }
});

root.addEventListener('input', (e) => {
  const target = e.target as HTMLElement;
  if (target.matches('input[type="range"][data-setting]')) {
    // During drag: update state + value in place, but DO NOT render().
    applySettingToState(target as HTMLInputElement);
    updateSliderValue(target as HTMLInputElement);
  }
});

wireFileInput(root, (file) => void handleFile(file));
wireDisclosures(root);

/** Show the full-size texture popover. The modal lives in the stable shell. */
function openTextureModal(src: string, caption: string): void {
  if (!src) return;
  const modal = document.getElementById('texture-modal');
  const img = document.getElementById('texture-modal-img') as HTMLImageElement | null;
  const cap = document.getElementById('texture-modal-caption');
  if (!modal || !img) return;
  img.src = src;
  if (cap) cap.textContent = caption;
  modal.classList.add('open');
}

function closeTextureModal(): void {
  const modal = document.getElementById('texture-modal');
  const img = document.getElementById('texture-modal-img') as HTMLImageElement | null;
  modal?.classList.remove('open');
  if (img) img.src = ''; // release the (large) data URL
}

// Esc closes the texture popover when it's open.
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && document.getElementById('texture-modal')?.classList.contains('open')) {
    closeTextureModal();
  }
});

render();
